import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Check, Play, BookOpen, Mail } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { FaFacebookF } from "react-icons/fa";
import ChatWidget from "./ChatWidget";
import leadsnow from "../assets/leadsnow-cleaning-logo.png";
import CleaningStepsSection from "./CleaningStepsSection";
import CleaningFaqAccordion from "./CleaningFaqAccordion";
import cart from "../assets/cleaning cart.png";
import shopping from "../assets/cleaningsup.png";
import dusty from "../assets/dusty.png";
import cleanershout from "../assets/cleanershout.png";
import vac from "../assets/vac.jpg";
import SecondForm from "./SecondForm";
import { useMemo, useState } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAppContext } from "../AppContext";
import plus from "../assets/ComCleanPlus.png";
import image99 from "../assets/$99 value big.png";
import image49 from "../assets/$49 Com Cleaning.png";
import marketing from "../assets/marketing-kit.png";
import telemarekting from "../assets/telemarekting.png";
import posty from "../assets/posty.png";
import brochure from "../assets/brochure.png";
import StripeFinalCodeCheck from "./StripeFinalCodeCheck";
import { EN_CLIENTS_PATH, EN_PROMOTE_PATH, ES_CLIENTS_PATH } from "../routes";

const CommercialCleaningLanding = () => {



  const faqItems = [
    {
      key: "1",
      label: "How can I get clients for my cleaning business if I’m just starting out?",
      children:
        "The fastest method is consistent outreach to verified decision-makers at businesses thar already outsource cleaning. This typically involves using commercial cleaning leads combined with phone calls, emails, or direct mail over several weeks.",
    },
    {
      key: "2",
      label: "How often should I follow up with potential cleaning clients?",
      children:
        "Most cleaning companies follow up 5–7 times over 30–45 days. Decision-makers are often busy, so repeated, professional follow-up increases the chance of a response without being intrusive.",
    },
    {
      key: "3",
      label: "Are commercial cleaning leads exclusive?",
      children:
        "Lead exclusivity depends on the provider. The most effective leads are not resold repeatedly and are delivered to one cleaning business at a time, reducing competition and improving response rates.",
    },
    {
      key: "4",
      label: "Do commercial cleaning leads actually work?",
      children:
        "Yes. Commercial cleaning leads work when they are accurate, well-targeted, and paired with consistent follow-up. Businesses that contact leads multiple times and track resultstend to see the best success.",
    },
    {
      key: "5",
      label: "What is the fastest way to get commercial cleaning clients?",
      children:
        "The fastest method is consistent outreach to verified decision-makers at businesses that already using commercial cleaning leads combined with phone calls, emails, and direct mail over several weeks.",
    },
    {
      key: "6",
      label: "What types of businesses hire commercial cleaning services most often?",
      children:
        "Common industries include office buildings, medical and dental practices, schools, retail stores, warehouses, and property management companies. These businesses typicaly have recurring cleaning needs.",
    },
    {
      key: "7",
      label: "Is cold calling still effective for cleaning businesses?",
      children:
        "Yes. When done professionally and with targeted leads, cold calling remains one of the most effective ways to introduce cleaning services and schedule walkthroughs or bids.",
    },
    {
      key: "8",
      label: "Can I use cleaning leads for email marketing instead of calling?",
      children:
        "Yes. Cleaning leads can be used for email outreach, especially when paired wirh a short introduction, service description, and follow-up sequence, Multi-channel outreach often produces better results than using only one method.",
    },
    {
      key: "9",
      label: "How many cleaning leads do I need to get new clients?",
      children:
        "The number varies by market and service type. Many cleaning companies start with 100–300 targeted leads and refine their messaging and approach based on response and conversion rates.",
    },
    {
      key: "10",
      label: "What should I say when contacting a potential cleaning clients",
      children:
        "Focus on introducing your company, confirming who manages cleaning services, and asking if they are open to reviewing options. The goal is to start a conversation, not close a deal on the first contact.",
    },
    {
      key: "11",
      label: "How long does it take to land a commercial cleaning contract?",
      children:
        "Some contracts are secured within weeks, while others take months. Consistency, follow-up, and professional presentation play a major role in timeline.",
    },
    {
      key: "12",
      label: "Are commercial cleaning leads better than online ads?",
      children:
        "Leads and ads serve different purposes. Leads give you direct access to decision-makers, while ads rely on prospects finding you. Many successful cleaning businesses use leads to build predictable growth without relying solely on advertising",
    },
    {
      key: "13",
      label: "Can this approach scale as my cleaning business grows?",
      children:
        "No. Many cleaning companies start with a simple spreadsheet or basic CRM to track outreach, responses, and follow-ups.",
    },
    {
      key: "14",
      label: "Do I need special software to manage cleaning leads?",
      children:
        "No special software is required to start. Many businesses use Excel, Google Sheets, or a basic CRM to stay organized. Consistency and follow-up matter more than tools."
    },
  ];

  function IconPill({ open }) {
    // blue outlined + like your image
    return (
      <span
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: 26,
          height: 26,
          borderRadius: 999,
          // border: "2px solid #1465B7",
          color: "#1465B7",
          fontWeight: 800,
          lineHeight: 1,
          fontSize: 28,
        }}
        aria-hidden="true"
      >
        +
        {/* {open ? "−" : "+"} */}
      </span>
    );
  }

  function MinusBullet() {
    // white minus bullet inside green answer like your image
    return (
      <span
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          // background: "rgba(255,255,255,0.22)",
          color: "#fff",
          fontWeight: 900,
          lineHeight: 1,
          fontSize: 24,
        }}
        aria-hidden="true"
      >
        −
      </span>
    );
  }

  function FaqCard({ item, open, onToggle }) {
    const CARD_BG = "#E4F7F9"; // light blue
    const BORDER = "rgba(38,169,74,0.40)"; // subtle green border
    const GREEN = "#2FA84F"; // answer box green

    return (
      <div className="w-full">
        {/* Question "pill" */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="w-full text-left rounded-full border shadow-sm transition"
          style={{
            background: CARD_BG,
            borderColor: BORDER,
            padding: "12px 14px",
          }}
        >
          <div className="flex items-center gap-3">
            <IconPill open={open} />
            <span className="text-[#1465B7] text-lg font-medium leading-snug">
              {item.label}
            </span>
          </div>
        </button>

        {/* Answer box */}
        <div
          className="overflow-hidden transition-[max-height,opacity,margin] duration-300 ease-in-out"
          style={{
            maxHeight: open ? 220 : 0,
            opacity: open ? 1 : 0,
            marginTop: open ? 10 : 0,
          }}
        >
          <div
            className="rounded-2xl shadow-sm"
            style={{
              background: GREEN,
              padding: "14px 16px",
            }}
          >
            <div className="flex gap-3 items-start">
              <MinusBullet />
              <p className="text-white text-lg leading-relaxed">{item.children}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const [openKeys, setOpenKeys] = useState(new Set());

  const { leftItems, rightItems } = useMemo(() => {
    const mid = Math.ceil(faqItems.length / 2);
    return {
      leftItems: faqItems.slice(0, mid),
      rightItems: faqItems.slice(mid),
    };
  }, []);

  const { buyNow, loadingHeader, setBuyNow, selectedPrice, setSelectedPrice, leadsCount, setLeadsCount } = useAppContext();

  const plans = [
    { qty: 50, price: 39 },
    { qty: 200, price: 99 },
    { qty: 500, price: 199 },
  ];


  return (
    <div>
      <Helmet>
        <title>How to Get Clients for Your Commercial Cleaning Business</title>
        <meta
          name="description"
          content="Learn how commercial cleaning companies consistently get clients using a proven step-by-step process and targeted commercial cleaning leads."
        />
        <link rel="alternate" hrefLang="en" href={`https://www.commercialcleaningleadsforyou.com${encodeURI(EN_CLIENTS_PATH)}`} />
        <link rel="alternate" hrefLang="es" href={`https://www.commercialcleaningleadsforyou.com${ES_CLIENTS_PATH}`} />
        <link rel="alternate" hrefLang="x-default" href="https://www.commercialcleaningleadsforyou.com/" />
        <script type="application/ld+json">
          {`
{
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://www.commercialcleaningleadsforyou.com/#organization",
      "name": "Leads Now Commercial Cleaning",
      "url": "https://www.commercialcleaningleadsforyou.com/",
      "description": "Leads Now Commercial Cleaning provides commercial cleaning leads and outreach-ready contact data to help cleaning businesses consistently acquire new clients.",
      "email": "leadsnowproducts@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "12020 Shamrock Plaza #200",
        "addressLocality": "Omaha",
        "addressRegion": "NE",
        "postalCode": "68154",
        "addressCountry": "US"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "leadsnowproducts@gmail.com",
        "availableLanguage": ["English"]
      }
    },

    {
      "@type": "WebSite",
      "@id": "https://www.commercialcleaningleadsforyou.com/#website",
      "url": "https://www.commercialcleaningleadsforyou.com/",
      "name": "Leads Now Commercial Cleaning",
      "publisher": { "@id": "https://www.commercialcleaningleadsforyou.com/#organization" },
      "inLanguage": "en-US"
    },

    {
      "@type": "WebPage",
      "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#webpage",
      "url": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business",
      "name": "How to Get Clients for Your Commercial Cleaning Business",
      "description": "A proven, step-by-step process commercial cleaning companies use to consistently win new clients, including targeting decision-makers, using commercial cleaning leads, and consistent multi-channel outreach.",
      "inLanguage": "en-US",
      "isPartOf": { "@id": "https://www.commercialcleaningleadsforyou.com/#website" },
      "publisher": { "@id": "https://www.commercialcleaningleadsforyou.com/#organization" },
      "mainEntity": [
        { "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#howto" },
        { "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#faq" },
        { "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#summary" },
        { "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#service" }
      ],
      "about": [
        { "@type": "Thing", "name": "Commercial cleaning" },
        { "@type": "Thing", "name": "Commercial cleaning leads" },
        { "@type": "Thing", "name": "Client acquisition" },
        { "@type": "Thing", "name": "Lead generation" }
      ]
    },

    {
      "@type": "HowTo",
      "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#howto",
      "name": "How to Get Clients for Your Commercial Cleaning Business",
      "description": "A repeatable, step-by-step approach used by successful commercial cleaning companies to consistently win new clients.",
      "inLanguage": "en-US",
      "totalTime": "P30D",
      "supply": [
        { "@type": "HowToSupply", "name": "Commercial cleaning leads (verified business contacts)" }
      ],
      "tool": [
        { "@type": "HowToTool", "name": "Phone" },
        { "@type": "HowToTool", "name": "Email" },
        { "@type": "HowToTool", "name": "Direct mail" },
        { "@type": "HowToTool", "name": "Follow-up tracking (CRM or spreadsheet)" }
      ],
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Define the type of cleaning clients you want",
          "text": "Clarify who you’re trying to reach. Successful cleaning companies focus on specific client types such as office buildings, medical or dental offices, schools and childcare facilities, warehouses and industrial buildings, retail stores and shopping centers, and property managers and multi-tenant facilities."
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Identify the decision-makers who control cleaning contracts",
          "text": "Cleaning contracts are typically decided by business owners, office managers, facility managers, and property managers. Many cleaning companies fail because they market to the wrong contacts. To get clients, you must reach people who can say yes."
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Use commercial cleaning leads to reach the right businesses",
          "text": "Commercial cleaning leads are verified business contacts that match your target market and allow you to reach decision-makers directly — without waiting for referrals or online inquiries. This removes the guesswork from prospecting."
        },
        {
          "@type": "HowToStep",
          "position": 4,
          "name": "Target by location, industry, and building type",
          "text": "Not all leads are equal. High-performing cleaning companies focus on targeted outreach, not mass messaging. Effective lead targeting includes city/county/ZIP code, type of business or facility, building size or square footage, and owner-occupied vs. managed properties."
        },
        {
          "@type": "HowToStep",
          "position": 5,
          "name": "Choose an outreach method and be consistent",
          "text": "Once you have the right leads, consistency matters more than perfection. Cleaning companies commonly reach prospects through phone outreach, email introductions, direct mail, and multi-touch follow-ups. The goal is not instant sales — it’s starting conversations."
        },
        {
          "@type": "HowToStep",
          "position": 6,
          "name": "Track responses and refine your approach",
          "text": "Successful cleaning businesses measure what works. Track who responds, which industries convert best, and which messages get callbacks. Over time, your process becomes more efficient and predictable."
        }
      ]
    },

    {
      "@type": "ItemList",
      "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#summary",
      "name": "Summary: How to Get Clients for Your Cleaning Business",
      "itemListOrder": "https://schema.org/ItemListOrderAscending",
      "numberOfItems": 5,
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Define your ideal customer" },
        { "@type": "ListItem", "position": 2, "name": "Reach the right decision-makers" },
        { "@type": "ListItem", "position": 3, "name": "Use targeted commercial cleaning leads" },
        { "@type": "ListItem", "position": 4, "name": "Stay consistent with outreach" },
        { "@type": "ListItem", "position": 5, "name": "Measure and refine your process" }
      ]
    },

    {
      "@type": "FAQPage",
      "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#faq",
      "url": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business",
      "name": "Frequently Asked Questions",
      "inLanguage": "en-US",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How can I get clients for my cleaning business if I’m just starting out?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The fastest method is consistent outreach to verified decision-makers at businesses that already outsource cleaning. This typically involves using commercial cleaning leads combined with phone calls, emails, or direct mail over several weeks."
          }
        },
        {
          "@type": "Question",
          "name": "How often should I follow up with potential cleaning clients?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most cleaning companies follow up 5–7 times over 30–45 days. Decision-makers are often busy, so repeated, professional follow-up increases the chance of a response without being intrusive."
          }
        },
        {
          "@type": "Question",
          "name": "What is the fastest way to get commercial cleaning clients?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The fastest method is consistent outreach to verified decision-makers at businesses that already outsource cleaning. This typically involves using commercial cleaning leads combined with phone calls, emails, or direct mail over several weeks."
          }
        },
        {
          "@type": "Question",
          "name": "What types of businesses hire commercial cleaning services most often?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Common industries include office buildings, medical and dental practices, schools, retail stores, warehouses, and property management companies. These businesses typically have recurring cleaning needs."
          }
        },
        {
          "@type": "Question",
          "name": "Is cold calling still effective for cleaning businesses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. When done professionally and with targeted leads, cold calling remains one of the most effective ways to introduce cleaning services and schedule walkthroughs or bids."
          }
        },
        {
          "@type": "Question",
          "name": "Can I use cleaning leads for email marketing instead of calling?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Cleaning leads can be used for email outreach, especially when paired with a short introduction, service description, and follow-up sequence. Multi-channel outreach often produces better results than using only one method."
          }
        },
        {
          "@type": "Question",
          "name": "What should I say when contacting a potential cleaning client?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Focus on introducing your company, confirming who manages cleaning services, and asking if they are open to reviewing options. The goal is to start a conversation, not close a deal on the first contact."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to land a commercial cleaning contract?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Some contracts are secured within weeks, while others take months. Consistency, follow-up, and professional presentation play a major role in timing."
          }
        },
        {
          "@type": "Question",
          "name": "Do commercial cleaning leads actually work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Commercial cleaning leads work when they are accurate, well-targeted, and paired with consistent follow-up. Businesses that contact leads multiple times and track results tend to see the best success."
          }
        },
        {
          "@type": "Question",
          "name": "Are commercial cleaning leads better than online ads?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Leads and ads serve different purposes. Leads give you direct access to decision-makers, while ads rely on prospects finding you. Many successful cleaning businesses use leads to build predictable growth without relying solely on advertising."
          }
        }
      ]
    },

    {
      "@type": "Service",
      "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#service",
      "name": "Commercial Cleaning Leads (500 Leads Instant Download)",
      "description": "Verified business contact leads that match your target market and help you reach commercial cleaning decision-makers. Pick your ZIP code, download and segment 500 leads, launch outreach, and track results.",
      "areaServed": { "@type": "Country", "name": "United States" },
      "provider": { "@id": "https://www.commercialcleaningleadsforyou.com/#organization" },
      "offers": {
        "@type": "Offer",
        "@id": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business/#offer",
        "url": "https://www.commercialcleaningleadsforyou.com/how%20to%20get%20clients%20for%20your%20commercial%20cleaning%20business",
        "priceCurrency": "USD",
        "price": "199.00",
        "availability": "https://schema.org/InStock",
        "category": "Lead Generation",
        "name": "Get Started For Just $199",
        "description": "Only $199 for 500 commercial cleaning leads. Instant download."
      }
    }
  ]
}`}
        </script>
      </Helmet>
      <header className="relative bg-gradient-to-br from-cyan-200 via-blue-200 to-cyan-300 overflow-hidden">
        <div className="px-4 pt-4 md:px-0 md:pt-0 md:absolute md:top-6 md:right-6 z-30">
          <div className="flex items-center gap-4">
            <LanguageSwitcher />

            <a
              href="https://www.facebook.com/profile.php?id=61582707424269"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-block"
              aria-label="Facebook"
            >
              <div className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#1877F2] shadow-lg ring-1 ring-black/10 transition-transform group-hover:scale-110 active:scale-95">
                <FaFacebookF className="text-white" size={24} />
              </div>
            </a>
          </div>
        </div>

        {/* Floating Bubbles */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-16 h-16 bg-white/20 rounded-full animate-bounce"></div>
          <div className="absolute top-32 right-20 w-12 h-12 bg-white/15 rounded-full animate-bounce"></div>
          <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-white/10 rounded-full animate-bounce"></div>
          <div className="absolute top-1/2 right-10 w-8 h-8 bg-white/25 rounded-full animate-bounce"></div>
          <div className="absolute bottom-32 right-1/3 w-14 h-14 bg-white/15 rounded-full animate-bounce"></div>
        </div>


        <div className="relative z-10 container mx-auto px-4 pb-8 pt-2 md:pt-8">
          <div className={`max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 ${(buyNow === 'payment' || buyNow === 'download') ? 'items-start' : 'items-center'}`}>
            {/* LEFT */}
            <div className="text-center lg:text-left min-w-0">
              <div className="mb-8 flex justify-center lg:justify-start">
                <img
                  src={leadsnow}
                  alt="Leads Now"
                  className="w-[280px] lg:w-[220px]"
                />
              </div>

              <h1 className="text-4xl font-semibold text-[#1465B7] leading-tight">
                How to Get Clients for Your Commercial Cleaning Business
              </h1>

              {/* {!(buyNow === 'payment' || buyNow === 'download') && ( */}
                <>
                  <div className="mt-5">
                    <h2 className="text-xl md:text-2xl font-medium mb-8 max-w-xl text-[#1465B7]">
                      If you're asking "How can I get
                      clients for my cleaning business?",
                      the answer isn't one tactic, it's a repeatable process
                    </h2>
                  </div>

                  <h3 className="mt-5 text-xl font-medium text-[#26A94A] mb-10 max-w-xl mx-auto lg:mx-0">
                    Below is a proven, step-by-step approach
                    used by successful commercial cleaning
                    companies to consistency win new clients
                  </h3>
                </>
              {/* )} */}
            </div>

            {/* RIGHT */}
            <div className="flex justify-end min-w-0">
               <div className={`flex-shrink-0 md:min-w-md w-full ${(buyNow === 'payment' || buyNow === 'download') ? 'lg:max-w-[28rem] lg:w-full' : 'lg:min-w-md lg:w-80'} items-center ${(buyNow === 'payment' || buyNow === 'download') ? '' : 'mt-40'}`}>
                {buyNow === 'home' && (
                  <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-3 border-[#26A94A]">
                    <div className="flex">
                      <button className="flex-1 bg-[#26A94A] text-white p-4 font-semibold  rounded-b-2xl">
                        <span className="text-white text-lg">Commercial Cleaning Leads For You</span>
                      </button>
                    </div>

                    <div className="px-5 py-4">
                      <p className="text-xl font-bold text-[#1465B7] mb-3 text-center font-semibold">
                        Get Your List Now
                      </p>
                      <p className="text-md text-gray-600 mb-4">
                        Send me {leadsCount} commercial cleaning business around my
                        <br /> zipcode{" "}
                      </p>

                      {/* 50 / 200 / 500 Leads row */}
                      <div className="flex justify-center mb-6">
                        <div
                          className="
                            w-full
                            bg-[#BFEAF2]
                            border-[3px] border-[#26A94A]
                            rounded-xl
                            overflow-hidden
                            grid grid-cols-3
                          "
                        >
                          {plans.map((p, idx) => (
                            <button
                              key={p.qty}
                              type="button"
                              onClick={() => {
                                setLeadsCount(p.qty);
                                setSelectedPrice(p.price * 100);
                              }}
                              className={`
                                py-2
                                flex flex-col items-center justify-center
                                transition
                                cursor-pointer
                                ${idx !== 0 ? "border-l-[3px] border-[#26A94A]" : ""}
                                ${leadsCount === p.qty ? "bg-[#85C1CF]" : ""}
                              `}
                            >
                              <span
                                className="
                                  text-lg
                                  font-extrabold leading-none
                                  text-[#EAFB55]
                                  drop-shadow-[1.5px_1.5px_0px_#2E7D32]
                                "
                              >
                                {p.qty}
                              </span>
                              <span className="text-sm font-extrabold leading-none text-[#1465B7]">
                                Leads
                              </span>
                              <span className="text-base sm:text-lg font-extrabold leading-none text-[#26A94A] my-1">
                                ${p.price}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="mt-8">
                          <button
                            className="w-full bg-[#FAF26B] hover:bg-yellow-300 text-black font-bold py-4 rounded-full transition-colors border-3 border-[#26A94A] cursor-pointer"
                            style={{ color: "#1465B7", fontSize: "28px" }}
                            onClick={() => setBuyNow('payment')}
                          >
                            Buy Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {(buyNow === 'payment' || buyNow === 'download') && <StripeFinalCodeCheck />}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Strategies Grid */}
      <section className="w-full py-10">
        <div className="mx-auto max-w-5xl px-4">
          {/* Heading (kept simple) */}

          {/* Steps grid: 3 columns like the image */}
          <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-3">
            {/* Step 1 */}
            <div className="rounded-lg border-2 border-[#26A94A] bg-[#E0F7FA]  p-4">
              <h2 className="mt-2 text-xl font-medium text-[#26A94A]">
                Step 1: Define the Type of Cleaning Clients You Want
              </h2>
              <p className="mt-2 text-lg leading-7 text-[#26A94A]">
                Before marketing your cleaning business, you need clarity on who you’re
                trying to reach
              </p>

              <p className="mt-3 text-base text-[#1465B7]">
                Successful cleaning companies focus on specific client types, such as:
              </p>

              <ul className="mt-2 list-disc space-y-1 pl-5 text-base leading-5 text-[#1465B7]">
                <li>Office buildings</li>
                <li>Medical or dental offices</li>
                <li>Schools and Childcare facilities</li>
                <li>Warehouses and industrial buildings</li>
                <li>Retail stroes and shopping enters</li>
                <li>Property managers and multi-tenant facilities</li>
              </ul>

              <p className="mt-3 text-base leading-5 text-[#1465B7]">
                The clearer your target, the easier it becomes to react decision-makers.
              </p>
            </div>

            {/* Step 2 */}
            <div className="rounded-lg border-2 border-[#26A94A] bg-[#E0F7FA]  p-4">
              <h2 className="mt-2 text-xl font-medium text-[#26A94A]">
                Step 2: Identify the Decision-Makers Who Control Cleaning
                Contracts
              </h2>

              <p className="mt-2 text-base leading-5 text-[#1465B7]">
                Cleaning contracts are typically decided by:
              </p>

              <ul className="mt-2 list-disc space-y-1 pl-5 text-base leading-5 text-[#1465B7]">
                <li>Business owners</li>
                <li>Office managers</li>
                <li>Facility managers</li>
                <li>Property managers</li>
              </ul>

              <p className="mt-3 text-base leading-5 text-[#1465B7]">
                Many cleaning companies fail because they market to the wrong
                contacts.
              </p>

              <p className="mt-3 text-base text-[#1465B7]">
                To get clients, you must reach people who can say yes.
              </p>
            </div>

            {/* Step 3 */}
            <div className="rounded-lg border-2 border-[#26A94A] bg-[#E0F7FA]  p-4">
              <h2 className="mt-2 text-xl font-medium text-[#26A94A]">
                Step 3: Use Commercial Cleaning Leads to Reach the Right Businesses
              </h2>

              <h3 className="mt-2 text-lg leading-7 text-[#26A94A]">
                One of the most effective ways to get cleaning clients
                is by using commercial cleaning leads.
              </h3>

              <p className="mt-3 text-base leading-6 text-[#1465B7]">
                Commercial cleaning leads are verified business contacts that
                match your target market and allow to reach
                decision makers directly --- without waiting for referrals or online inquries.<br />
                This removes the guesswork from prospecting
              </p>
            </div>

            {/* Step 4 */}
            <div className="rounded-lg border-2 border-[#26A94A] bg-[#E0F7FA]  p-4">
              <h2 className="mt-2 text-xl font-medium text-[#26A94A]">
                Step 4: Target by Location, Industry, and Building Type
              </h2>

              <h3 className="mt-2 text-lg leading-7 text-[#26A94A]">
                Not all leads are equal. High-performing cleaning companies focus on targeted outreach, not
                mass messaging
              </h3>


              <p className="mt-3 text-base text-[#1465B7]">
                Effective lead targetting includes:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-base leading-5 text-[#1465B7]">
                <li>City, county, or ZIP code</li>
                <li>Type of business or facility</li>
                <li>Building size or square footage</li>
                <p>Owners occupies vs managed properties</p>
              </ul>

              <p className="mt-3 text-base leading-5 text-[#1465B7]">
                Targeting improves response rates and reduces wasted effort.
              </p>
            </div>

            {/* Step 5 */}
            <div className="rounded-lg border-2 border-[#26A94A] bg-[#E0F7FA]  p-4">
              <h2 className="mt-2 text-xl font-medium text-[#26A94A]">
                Step 5: Choose an Outreach Method and Be Consistent
              </h2>

              <h3 className="mt-2 text-lg leading-7 text-[#26A94A]">
                Once you have the right leads, consistency matters more than
                perfection.
              </h3>

              <p className="mt-3 text-base text-[#1465B7]">
                Cleaning companies commonly reach prospects through:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-base leading-5 text-[#1465B7]">
                <li>Phone outreach</li>
                <li>Email introductions</li>
                <li>Direct mail</li>
                <li>Multi-touch follow-ups</li>
              </ul>

              <p className="mt-3 text-base leading-5 text-[#1465B7]">
                The goal is not instant sales -- it's starting conversations.
              </p>
            </div>

            {/* Step 6 */}
            <div className="rounded-lg border-2 border-[#26A94A] bg-[#E0F7FA]  p-4">
              <h2 className="mt-2 text-xl font-medium text-[#26A94A]">
                Step 6: Track Responses and Refine Your Approach
              </h2>

              <h3 className="mt-2 text-lg leading-7 text-[#26A94A]">
                Successful cleaning businesses measure what works.
              </h3>

              <p className="mt-3 text-base text-[#1465B7]">
                Track:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-base leading-5 text-[#1465B7]">
                <li>Who responds</li>
                <li> Which industries convert best</li>
                <li>Which messages get callbacks</li>
              </ul>

              <p className="mt-3 text-base leading-5 text-[#1465B7]">
                Over time, your process becomes more efficient and predictable.
              </p>
            </div>
          </div>


        </div>
      </section>


      <section className="w-full pb-10">
        <div className="mx-auto max-w-5xl px-4">
          {/* Light panel */}
          <div className="rounded-xl border-2 border-[#26A94A] bg-[#E0F7FA] px-6 py-6 sm:py-8">
            {/* 3-column flex: Left image | Center content | Right image */}
            <div className="flex items-center justify-between gap-6">
              {/* Left Illustration */}
              <div className="hidden shrink-0 sm:block ml-16">
                <img
                  src={vac}
                  alt="Cleaner illustration"
                  className="h-[120px] w-auto md:h-[140px]"
                  draggable={false}
                />
              </div>

              {/* Center Content */}
              <div className="flex-1 text-center">
                <h2 className="text-[18px] font-extrabold text-[#26A94A] sm:text-[20px]">
                  Why This Process Works for Cleaning Businesses
                </h2>

                <p className="mx-auto mt-2 max-w-2xl text-base text-[#26A94A]">
                  This step-by-step method works because it:
                </p>

                <ul className="mx-auto mt-4 inline-block list-disc space-y-1 pl-5 text-left text-base text-[#1465B7]">
                  <li>Focuses on decision-makers, not random contacts</li>
                  <li>Creates a steady pipeline of potential clients</li>
                  <li>Redues dependence on referalls or ads</li>
                  <li>Scales as your business grows</li>
                </ul>

                <p className="mx-auto mt-4 text-base text-[#26A94A] sm:text-[14px]">
                  It&apos;s repeatable, measurable, and controllable
                </p>

                {/* CTA */}
                {/* <div className="mt-5 flex justify-center">
                  <button
                    type="button"
                    className="rounded-full border-2 border-[#1E7A4C] bg-[#FFE96A] px-10 py-3 text-[14px] font-extrabold text-[#1465B7] shadow-[0_3px_0_rgba(0,0,0,0.12)] transition hover:brightness-95 active:translate-y-[1px] active:shadow-[0_2px_0_rgba(0,0,0,0.12)]"
                    style={{ color: "#1465B7" }}
                  >
                    Get Started For Just
                  </button>
                </div> */}
              </div>

              {/* Right Illustration */}
              <div className="hidden shrink-0 sm:block mr-16">
                <img
                  src={dusty}
                  alt="Cleaner illustration"
                  className="h-[120px] w-auto md:h-[140px]"
                  draggable={false}
                />
              </div>
            </div>

            {/* Optional: show images stacked on mobile */}
            <div className="mt-6 flex items-center justify-center gap-6 sm:hidden">
              <img
                src={vac}
                alt="Cleaner illustration"
                className="h-[92px] w-auto"
                draggable={false}
              />
              <img
                src={dusty}
                alt="Cleaner illustration"
                className="h-[92px] w-auto"
                draggable={false}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-[#BFEAF2] py-10">
        <div className="relative mx-auto max-w-5xl px-4">
          <div className="absolute inset-0">
            <div className="absolute top-10 left-10 w-16 h-16 bg-white/20 rounded-full animate-bounce"></div>
            <div className="absolute top-32 right-20 w-12 h-12 bg-white/15 rounded-full animate-bounce"></div>
            <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-white/10 rounded-full animate-bounce"></div>
            <div className="absolute top-1/2 right-10 w-8 h-8 bg-white/25 rounded-full animate-bounce"></div>
            <div className="absolute bottom-32 right-1/3 w-14 h-14 bg-white/15 rounded-full animate-bounce"></div>
          </div>
          {/* Mint panel */}
          <div className="px-6 py-8">
            <h2 className="text-center text-xl font-extrabold text-[#1465B7] sm:text-xl">
              Why This Process Works for Cleaning Businesses
            </h2>

            <h4 className="mx-auto mt-4 max-w-3xl text-center text-lg leading-7 text-[#26A94A]">
              If you want to apply this process quickly, commercial cleaning
              leads give you a head start by providing access to businesses that
              already fit your criteria.
            </h4>

            <p className="mt-5 text-center text-base font-semibold text-[#1465B7]">
              You choose:
            </p>

            <ul className="mx-auto mt-3 max-w-sm list-disc space-y-2 pl-24 text-base leading-6 text-[#1465B7]">
              <li>Where you want to work</li>
              <li>Who you want to serve</li>
              <li>How fast you want to grow</li>
            </ul>

            {/* CTA */}
            {/* <div className="mt-7 flex justify-center">
              <button
                className="rounded-3xl border-2 border-[#1E7A4C] bg-[#FFE96A] px-8 py-3 text-sm font-extrabold text-[#1E7A4C] shadow-sm hover:brightness-95"
                style={{ color: "#1465B7" }}
              >
                Get Started For Just
              </button>
            </div> */}
          </div>
        </div>
      </section>

      {/* <CleaningFaqAccordion /> */}
      <div className="max-w-5xl mx-auto">
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl text-[#1465B7] font-semibold">Frequently Asked Questions</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6 px-4">
            <div className="space-y-4">
              {leftItems.map((item) => (
                <FaqCard
                  key={item.key}
                  item={item}
                  open={openKeys.has(item.key)}
                  onToggle={() => {
                    setOpenKeys((prev) => {
                      const next = new Set(prev);
                      if (next.has(item.key)) {
                        next.delete(item.key);
                      } else {
                        next.add(item.key);
                      }
                      return next;
                    });
                  }}
                />
              ))}
            </div>

            <div className="space-y-4">
              {rightItems.map((item) => (
                <FaqCard
                  key={item.key}
                  item={item}
                  open={openKeys.has(item.key)}
                  onToggle={() => {
                    setOpenKeys((prev) => {
                      const next = new Set(prev);
                      if (next.has(item.key)) {
                        next.delete(item.key);
                      } else {
                        next.add(item.key);
                      }
                      return next;
                    });
                  }}
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className="relative w-full bg-[#bfeaf0] py-14">
        {/* subtle cloud/bubble decoration */}
        <div className="absolute top-10 left-10 w-16 h-16 bg-white/20 rounded-full animate-bounce"></div>
        <div className="absolute top-32 right-20 w-12 h-12 bg-white/15 rounded-full animate-bounce"></div>
        <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-white/10 rounded-full animate-bounce"></div>
        <div className="absolute top-1/2 right-10 w-8 h-8 bg-white/25 rounded-full animate-bounce"></div>
        <div className="absolute bottom-32 right-1/3 w-14 h-14 bg-white/15 rounded-full animate-bounce"></div>

        <div className="mx-auto w-full max-w-5xl px-4">
          {/* card */}
          <div className="rounded-2xl px-6 py-8shadow-[0_2px_0_rgba(0,0,0,0.06)] md:px-10">
            <div className="flex flex-col items-center gap-7 md:flex-row md:items-center md:gap-10">
              {/* Illustration */}
              <div className="shrink-0">
                <div className="flex items-center justify-center">
                  <img
                    src={shopping}
                    alt="Cleaning tools illustration"
                    className="h-[230px] w-[230px] select-none"
                    draggable={false}
                  />
                </div>
              </div>

              {/* Text */}
              <div className="w-full text-center md:text-left mx-auto md:ml-28 md:mx-0">
                <h2 className="text-xl font-bold leading-tight text-[#1465B7]">
                  Summary: How to Get Clients for Your Cleaning Business
                </h2>

                <p className="mt-2 text-base text-[#26A94A]">
                  To get clients for your cleaning business:
                </p>

                <div className="mt-2 space-y-1 text-base text-[#2ea44f]">

                  <div className="mx-auto mt-1 space-y-1 pl-5 text-left md:mx-0 text-[#1465B7]">
                    1. Define your ideal customer
                    <br />
                    2. Reach the right decision-makers
                    <br />
                    3. Use targeted commercial cleaning leads
                  </div>
                  <div className="mx-auto space-y-1 pl-5 text-left md:mx-0 text-[#1465B7]">
                    4. Stay consistent with outreach
                    <br />
                    5. Measure and refine your results
                  </div>
                  {/* Button */}
                  {/* <div className="mt-7">
                    <button
                      type="button"
                      className="rounded-full border-2 border-[#1f6fbf] bg-[#fff06a] px-8 py-3 text-[14px] font-extrabold text-[#1f6fbf] shadow-[0_3px_0_rgba(0,0,0,0.12)] transition hover:brightness-105 active:translate-y-[1px] active:shadow-[0_2px_0_rgba(0,0,0,0.12)]"
                      style={{ color: "#1465B7" }}
                    >
                      Get Started For Just
                    </button>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <SecondForm /> */}

      <section className="py-16 bg-[#DBE4E5]">
        <div className="relative z-10 container mx-auto px-4 py-8">
          {/* <div className="flex flex-col lg:flex-row justify-center gap-8"> */}
          <div className="block lg:flex max-w-5xl mx-auto">

            {/* Left Content */}
            <div className="flex-1  max-w-xl text-center lg:text-left">
              <div className="mb-3 max-w-lg">
                <p className="text-[#26A94A] font-bold text-3xl ">
                  {" "}
                  Get Started:{" "}
                  <span className="text-[#1465B7]">
                    Pick your zip code, <br /> we do the rest!
                  </span>
                </p>
                {/* </div> */}
                <div>
                  <ul className="list-disc ml-5 text-lg text-gray-700">
                    <li>
                      Download and segment inside the 500 commercial <br />
                      cleaning leads based on your marketing goals.
                    </li>

                    <li className="my-2">
                      Launch your outreach through email, ads, calls, or <br />{" "}
                      direct mail.
                    </li>

                    <li>
                      Track your results and fine-tune for even better <br />{" "}
                      outcomes.
                    </li>
                  </ul>
                </div>
                <div className="mt-3">
                  <p className="text-gray-700 text-lg leading-normal gap-y-2">
                    You don’t need an agency. You don’t need a big <br />{" "}
                    marketing team. Just the right list and a smart plan. It’s{" "}
                    <br /> that simple: You get the list. You do the outreach.
                    <br />
                    And you keep 100% of the profits.
                  </p>
                </div>

                <div className="font-semibold text-[#26A94A] text-lg">
                  <p>
                    Only ${selectedPrice / 100} for {leadsCount} leads.
                    <br />
                    Commercial Cleaning Leads For You
                  </p>
                </div>
              </div>

            </div>

            {/* Right Form */}
            {buyNow === 'home' &&
              <div className="flex-shrink-0 md:min-w-md lg:min-w-md w-full lg:w-80">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-3 border-[#26A94A]">
                  <div className="flex">
                    <button className="flex-1 bg-[#26A94A] text-white p-4 font-semibold  rounded-b-2xl">
                      <span className="text-white text-lg">Commercial Cleaning Leads For You</span>
                    </button>
                  </div>

                  <div className="px-5 py-4">
                    <p className="text-xl font-bold text-[#1465B7] mb-3 text-center font-semibold">
                      Get Your List Now
                    </p>
                    <p className="text-md text-gray-600 mb-4">
                      Send me {leadsCount} commercial cleaning business around my
                      <br /> zipcode{" "}
                    </p>

                    {/* 50 / 200 / 500 Leads row */}
                    <div className="flex justify-center mb-6">
                      <div
                        className="
                          w-full
                          bg-[#BFEAF2]
                          border-[3px] border-[#26A94A]
                          rounded-xl
                          overflow-hidden
                          grid grid-cols-3
                        "
                      >
                        {plans.map((p, idx) => (
                          <button
                            key={p.qty}
                            type="button"
                            onClick={() => {
                              setLeadsCount(p.qty);
                              setSelectedPrice(p.price * 100);
                            }}
                            className={`
                              py-2
                              flex flex-col items-center justify-center
                              transition
                              cursor-pointer
                              ${idx !== 0 ? "border-l-[3px] border-[#26A94A]" : ""}
                              ${leadsCount === p.qty ? "bg-[#85C1CF]" : ""}
                            `}
                          >
                            <span
                              className="
                                text-lg
                                font-extrabold leading-none
                                text-[#EAFB55]
                                drop-shadow-[1.5px_1.5px_0px_#2E7D32]
                              "
                            >
                              {p.qty}
                            </span>
                            <span className="text-sm font-extrabold leading-none text-[#1465B7]">
                              Leads
                            </span>
                            <span className="text-base sm:text-lg font-extrabold leading-none text-[#26A94A] my-1">
                              ${p.price}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="mt-8">
                        <button
                          className="w-full bg-[#FAF26B] hover:bg-yellow-300 text-black font-bold py-4 rounded-full transition-colors border-3 border-[#26A94A] cursor-pointer"
                          style={{ color: "#1465B7", fontSize: "28px" }}
                          onClick={() => {
                            setBuyNow('payment');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          Buy Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}
            {loadingHeader && <div className="flex min-h-screen">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            </div>}
            {/* Top header already mounts StripeFinalCodeCheck — avoid duplicate API calls */}
            {buyNow === "download" && <StripeFinalCodeCheck />}
            {buyNow === "payment" && (
              <button
                type="button"
                className="w-full bg-[#FAF26B] hover:bg-yellow-300 text-[#1465B7] font-bold py-4 rounded-full border-3 border-[#26A94A] cursor-pointer"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                Complete payment above ↑
              </button>
            )}

          </div>
        </div>
        <div className="container mx-auto max-w-5xl px-4 md:px-0">
          <div className="text-center mb-12">
            {/* <div className="flex mx-auto items-center gap-5 justify-between"> */}
            {/* <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5 justify-center sm:justify-between mx-auto">
   
                <img src={plus} width={200} height={125} />
               <div className="mt-4">
                 <p className="text-3xl text-[#1465B7] font-bold">
                   Get These <span className="text-[#26A94A]">Extras</span> With
                   Every Order
                 </p>
               </div>
               <img src={plus} width={200} height={125} />
             </div>
    */}
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5 justify-center sm:justify-between mx-auto">
              <img src={plus} width={250} height={158} />

              <div className="mt-4">
                <p className="text-3xl text-[#1465B7] font-bold">
                  Get These <span className="text-[#26A94A]">Extras</span> With Every Order
                </p>
              </div>

              <img src={plus} width={250} height={158} className="hidden sm:block" />
            </div>
            <div className="container mx-auto max-w-5xl px-4 md:px-0 mt-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-9 max-w-4xl justify-center mx-auto">
                {/* <div>
                 <div className="w-40 h-40 rounded-full flex items-center justify-center mx-auto">
                   <img src={marketing} width={130}/>
                 </div>
                 <p className="text-[#1465B7] font-semibold text-lg leading-relaxed text-center">
                   The Commercial <br/> Cleaning Marketing <br/>Made Easy <br/> Marketing Kit !
                 </p>
               </div> */}
                <div>
                  <div className="relative flex justify-center items-center">
                    {/* First Image */}
                    <div className="w-40 h-40 rounded-full flex items-center justify-center  z-20">
                      <img src={image99} width={130} height={71} />
                    </div>
                    <div className="w-40 h-40 rounded-full flex items-center justify-center -ml-12 z-10">
                      <img src={marketing} width={130} height={122} />
                    </div>

                    {/* Second Image - overlapping the first */}
                  </div>

                  <p className="text-[#1465B7] font-semibold text-lg leading-relaxed text-center mt-4 ml-4">
                    The Commercial <br />
                    Cleaning Marketing <br />
                    Made Easy <br />
                    Marketing Kit!
                  </p>
                </div>

                <div>
                  <div className="w-40 h-40 rounded-full flex items-center justify-center mx-auto">
                    <img src={telemarekting} width={130} height={119} />
                  </div>
                  <p className="text-[#1465B7] font-semibold text-lg leading-relaxed text-center">
                    Bonus Voice Mail <br /> Script
                  </p>
                </div>
                <div>
                  <div className="w-40 h-40 rounded-full flex items-center justify-center mx-auto">
                    <img src={posty} width={130} height={98} />
                  </div>
                  <p className="text-[#1465B7] font-semibold text-lg leading-relaxed text-center">
                    Bonus Postcard <br /> Templates
                  </p>
                </div>
                <div>
                  {/* <div className="w-40 h-40 rounded-full flex items-center justify-center mx-auto">
                     <img src={brochure} width={130} />
                   </div> */}
                  <div className="relative flex justify-center items-center">
                    {/* First Image */}
                    <div className="w-40 h-40 rounded-full flex items-center justify-center  z-20">
                      <img src={image49} width={130} height={77} />
                    </div>
                    <div className="w-40 h-40 rounded-full flex items-center justify-center -ml-12 z-10">
                      <img src={brochure} width={130} />
                    </div>

                    {/* Second Image - overlapping the first */}
                  </div>
                  <p className="text-[#1465B7] font-semibold text-lg leading-relaxed text-center ml-6">
                    Bonus Brouchure <br /> Templates
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related guides - other two pages only (this page = Get Clients) */}
      <div className="bg-[#e8f4f6] py-6 border-t border-cyan-100">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-[#1465B7] font-semibold text-sm uppercase tracking-wide mb-3 text-center">
            Related Guides
          </p>
          <p className="related-guides-links flex flex-wrap justify-center gap-x-4 gap-y-2 items-center text-center">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="text-[#26A94A] hover:text-[#1465B7] transition">Home</Link>
            <Link to={EN_PROMOTE_PATH} onClick={() => window.scrollTo(0, 0)} className="text-[#26A94A] hover:text-[#1465B7] transition">How to Promote Your Commercial Cleaning Business</Link>
          </p>
        </div>
      </div>

      <div className="bg-[#bfeaf0]  py-16 mx-auto">
        <div className="text-base max-w-5xl mx-auto text-[#26A94A] text-center">
          <p>
            For any issues contact support at:{" "}
            <span className="underline">leadsnowproducts@gmail.com</span>
          </p>
          <div className="mt-2 text-base">
            <p>
              Leads Now Commercial Cleaning 12020 Shamrock Piz - 200 Omaha, NE
              68154
            </p>
          </div>
        </div>
      </div>

      <ChatWidget />
    </div>
  );
};

export default CommercialCleaningLanding;
